/**
 * IRL Review panel — the existing IRL upload + mapping + review + folder-
 * allowlist flow. Extracted from the previous IRLSection.tsx as part of
 * the IRL Toolbox refactor; behavior is intentionally unchanged.
 *
 * The "Generate IRL" entry point lives in its own toolbox tile now and is
 * NOT mounted from this panel.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { IRL_FIELDS, type IRLFieldKey, type IRLRequest, type IRLMeta, type IRLStatus, type IRLPriority } from "../types";
import { parseIRLFile, type ParsedSheet } from "../parser";
import { buildRequests, findBestHeaderRow, isMappingValid, suggestMapping, type Mapping } from "../autoMap";
import { roundTripSurgical } from "../excelSurgical";
import {
  clearIRLRequests,
  getIRLMatchExcludedFolders,
  getIRLMatchPanelOpen,
  getIRLMeta,
  getIRLRequests,
  isIRLMatchSelectionInitialized,
  saveIRLMatchExcludedFolders,
  saveIRLMatchPanelOpen,
  saveIRLRequests,
} from "../store/irlStore";
import { getFolderRef, getIndexMap, getIndexRows } from "../../store/folderStore";
import {
  buildFolderTree,
  collectSubtreeCodes,
  isCodeExcluded,
  nodeExclusion,
  type FolderNode,
} from "../match/folderTree";
import { fetchSharePointTree, type SPTreeNode } from "../../utils/sharePointTree";
import {
  clearIRLAnswers,
  getIRLAnswers,
  saveIRLAnswers,
  type DocumentMatch,
  type IRLAnswer,
} from "../store/irlAnswersStore";
import IRLNotesAdder from "./IRLNotesAdder";
import IRLDatabookAdder from "./IRLDatabookAdder";
import { getAgendaDatabookPin } from "../../fdd/tools/agenda/storage";
import { apiFetch } from "../../api/fddClient";

type Props = {
  projectId: string;
};

const STATUS_COLORS: Record<IRLStatus, { bg: string; fg: string; label: string }> = {
  open: { bg: "#fef9e7", fg: "#b7950b", label: "Open" },
  fulfilled: { bg: "#dff3d9", fg: "#2f6b2f", label: "Fulfilled" },
  partial: { bg: "#fff4e6", fg: "#9a3412", label: "Partial" },
  withdrawn: { bg: "#f1f5f9", fg: "#475569", label: "Withdrawn" },
};

const PRIORITY_COLORS: Record<IRLPriority, { bg: string; fg: string; label: string }> = {
  high: { bg: "#fee2e2", fg: "#991b1b", label: "High" },
  medium: { bg: "#fef9c3", fg: "#854d0e", label: "Medium" },
  low: { bg: "#e0f2fe", fg: "#075985", label: "Low" },
};

const PREVIEW_ROW_LIMIT = 100;

const cellPadStyle: React.CSSProperties = {
  padding: "4px 6px",
  verticalAlign: "top",
};

const editInputStyle: React.CSSProperties = {
  width: "100%",
  padding: "4px 6px",
  border: "1px solid #cfe6c7",
  borderRadius: 4,
  fontSize: 13,
  background: "#fff",
  color: "#333",
  fontFamily: "inherit",
  boxSizing: "border-box",
};
const COL_MIN_WIDTH = 160;

const filterSelectStyle: React.CSSProperties = {
  padding: "5px 8px",
  border: "1px solid #cfe6c7",
  borderRadius: 6,
  fontSize: 12,
  background: "#fff",
  color: "#2f6b2f",
  maxWidth: 200,
};

const FilterSelect: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (next: string) => void;
  /** Optional one-click shortcut value (e.g., "Leo Berwick" for advisor). */
  quickPick?: string;
  quickPickLabel?: string;
}> = ({ label, value, options, onChange, quickPick, quickPickLabel }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
    <label style={{ color: "#4a7d4a" }}>{label}:</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} style={filterSelectStyle}>
      <option value="all">All</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
    {quickPick && quickPickLabel && value !== quickPick && (
      <button
        onClick={() => onChange(quickPick)}
        title={`Filter to ${quickPick}`}
        style={{
          background: "#15803d",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          padding: "4px 8px",
          fontSize: 10,
          fontWeight: 700,
          cursor: "pointer",
          letterSpacing: 0.3,
        }}
      >
        {quickPickLabel}
      </button>
    )}
  </div>
);

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  // 0x8000 chunk avoids "Maximum call stack size exceeded" on large files.
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(bin);
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function findNode(roots: FolderNode[], code: string): FolderNode | null {
  for (const root of roots) {
    if (root.code === code) return root;
    const inChild = findNode(root.children, code);
    if (inChild) return inChild;
  }
  return null;
}

function isAncestorOf(maybeAncestor: string, descendant: string): boolean {
  return descendant === maybeAncestor || descendant.startsWith(maybeAncestor + ".");
}

function countMatches(map: Record<string, IRLAnswer>): number {
  let n = 0;
  for (const a of Object.values(map)) n += a.matches.length;
  return n;
}

function countWithMatches(map: Record<string, IRLAnswer>): number {
  return Object.values(map).filter((a) => a.matches.length > 0).length;
}

const CONFIDENCE_STYLE: Record<DocumentMatch["confidence"], { bg: string; fg: string }> = {
  high: { bg: "#dff3d9", fg: "#15803d" },
  medium: { bg: "#fff4e6", fg: "#9a3412" },
  low: { bg: "#f1f5f9", fg: "#475569" },
};

const IRLReviewPanel: React.FC<Props> = ({ projectId }) => {
  // Persisted state
  const [requests, setRequests] = useState<IRLRequest[]>(() => getIRLRequests(projectId));
  const [meta, setMeta] = useState<IRLMeta | null>(() => getIRLMeta(projectId));

  // Import flow state
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const [headerRowIdx, setHeaderRowIdx] = useState(0);
  const [mapping, setMapping] = useState<Mapping>([]);
  const [manualMapping, setManualMapping] = useState<boolean[]>([]);

  // Review filters. Status keeps its existing pill-bar shape; the others
  // are categorical multi-select-feel single-selects ("All" + observed
  // values). Filtering NEVER changes the underlying request list —
  // `originalRowIndex` is preserved, so export still hits the right rows.
  const [statusFilter, setStatusFilter] = useState<"all" | IRLStatus>("all");
  const [advisorFilter, setAdvisorFilter] = useState<string>("all");
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [topicFilter, setTopicFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<"all" | IRLPriority>("all");
  const [requestSearch, setRequestSearch] = useState<string>("");

  // Document Match state — Phase 1 (name-based) results per IRL request id.
  const [answers, setAnswers] = useState<Record<string, IRLAnswer>>(() => getIRLAnswers(projectId));
  const [searching, setSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Per-row edit toggle — only IDs in this set render their cells as
  // editable inputs. Read-only by default so the table stays scan-friendly.
  const [editingRowIds, setEditingRowIds] = useState<Set<string>>(() => new Set());

  // Notes-adder inline section (toggled by the "Add from notes" header button).
  const [notesOpen, setNotesOpen] = useState(false);
  // Databook-adder inline section.
  const [databookOpen, setDatabookOpen] = useState(false);

  // Enable the import button only when a databook is pinned with an IRL column detected.
  const canImportFromDatabook = useMemo(() => {
    const pin = getAgendaDatabookPin(projectId);
    if (!pin) return false;
    return Object.values(pin.irlColByTab ?? {}).some((v) => v != null);
  }, [projectId]);

  // IDs of rows just added from the most recent notes batch. Highlighted in
  // green + auto-scrolled to so it's obvious what just landed. Replaced (not
  // accumulated) on each Add — the user only needs to see the latest batch.
  const [recentlyAddedIds, setRecentlyAddedIds] = useState<Set<string>>(() => new Set());
  const reviewTableRef = useRef<HTMLDivElement | null>(null);
  const toggleEdit = (id: string) =>
    setEditingRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Folder picker state — codes (any depth) the user opted out of, plus
  // which tree nodes are expanded, plus whether the whole panel is open.
  const [excludedFolders, setExcludedFolders] = useState<Set<string>>(
    () => new Set(getIRLMatchExcludedFolders(projectId)),
  );
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [panelOpen, setPanelOpen] = useState<boolean>(() => getIRLMatchPanelOpen(projectId));

  // Reload persisted state when project changes
  useEffect(() => {
    setRequests(getIRLRequests(projectId));
    setMeta(getIRLMeta(projectId));
    setExcludedFolders(new Set(getIRLMatchExcludedFolders(projectId)));
    setPanelOpen(getIRLMatchPanelOpen(projectId));
    setExpanded(new Set()); // reset tree open-state per project
    setAnswers(getIRLAnswers(projectId));
    setSearchProgress(null);
    setSearchError(null);
    setEditingRowIds(new Set());
    setNotesOpen(false);
    setRecentlyAddedIds(new Set());
    setParsed(null);
    setImportFile(null);
    setParseError(null);
    setStatusFilter("all");
    setAdvisorFilter("all");
    setEntityFilter("all");
    setTopicFilter("all");
    setPriorityFilter("all");
    setRequestSearch("");
  }, [projectId]);

  // Auto-scroll the review table to the bottom when new rows are added so
  // the user sees the highlighted additions without manual scrolling.
  useEffect(() => {
    if (recentlyAddedIds.size === 0) return;
    const el = reviewTableRef.current;
    if (!el) return;
    // Defer one frame so the new rows are in the DOM before we measure.
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [recentlyAddedIds]);

  // Tree of folders derived from the project's saved index. Top-level depth = 1.
  // We pull both indexRows (preferred — has titles) AND indexMap keys (fallback —
  // populated when the project was set up via SharePoint scan rather than an
  // uploaded index file).
  const folderTree = useMemo<FolderNode[]>(() => {
    const indexRows = getIndexRows(projectId) || [];
    const indexMap = getIndexMap(projectId) || {};
    return buildFolderTree(indexRows, Object.keys(indexMap));
    // Re-derive when project changes or when meta changes (proxy for the
    // user saving/clearing an IRL list — index can shift around then).
  }, [projectId, meta]);

  // Initial expanded state: every node at depth 1 (top level), so depth-2
  // children are visible by default. Deeper levels stay collapsed behind +.
  // Only seed when the tree first becomes non-empty for this project — never
  // re-seed on every render or we'd undo the user's clicks.
  useEffect(() => {
    if (folderTree.length === 0) return;
    setExpanded((prev) => {
      if (prev.size > 0) return prev;
      const next = new Set<string>();
      for (const root of folderTree) next.add(root.code);
      return next;
    });
  }, [folderTree]);

  // One-time seeding: fresh projects start with EVERY top-level folder
  // excluded so the matchers are opt-in. The user has to actively pick the
  // folders they want analyzed before either matcher will run. Persists
  // immediately so downstream callers (FolderMirroring, useIrlMatches) see
  // the seeded state on the very next read.
  useEffect(() => {
    if (folderTree.length === 0) return;
    if (isIRLMatchSelectionInitialized(projectId)) return;
    const seeded = new Set<string>();
    for (const root of folderTree) seeded.add(root.code);
    persistExcluded(seeded);
  }, [projectId, folderTree]);

  // ── Selection helpers ────────────────────────────────────────────────────
  const persistExcluded = (next: Set<string>) => {
    setExcludedFolders(next);
    saveIRLMatchExcludedFolders(projectId, Array.from(next));
  };

  // Toggle a single node's checkbox (tristate-aware).
  const toggleNode = (node: FolderNode) => {
    const next = new Set(excludedFolders);
    const subtree = collectSubtreeCodes(node);
    const status = nodeExclusion(node, excludedFolders);

    if (status === "included") {
      // Currently fully included → exclude entire subtree. We add the node
      // itself (which implicitly excludes descendants via ancestor walk) and
      // strip any redundant descendant entries.
      next.add(node.code);
      for (const c of subtree) {
        if (c !== node.code) next.delete(c);
      }
    } else if (status === "excluded") {
      // Re-include. Two sub-cases:
      //   (a) this node is in the set — remove it.
      //   (b) an ancestor is in the set — re-expand that ancestor into its
      //       direct children minus the path leading to `node`, then remove
      //       any descendants of `node` that were incidentally added.
      if (next.has(node.code)) {
        next.delete(node.code);
      } else {
        let anc: string | null = node.code;
        while (anc) {
          const i = anc.lastIndexOf(".");
          anc = i === -1 ? null : anc.slice(0, i);
          if (anc && next.has(anc)) {
            // Find that ancestor in the tree, push its direct children into
            // the excluded set (except the one we descended through), and
            // remove the ancestor itself.
            const ancNode = findNode(folderTree, anc);
            next.delete(anc);
            if (ancNode) {
              for (const child of ancNode.children) {
                if (!isAncestorOf(child.code, node.code) && child.code !== node.code) {
                  next.add(child.code);
                }
              }
            }
            break;
          }
        }
      }
      // Remove any subtree descendants that were left dangling.
      for (const c of subtree) {
        if (c !== node.code) next.delete(c);
      }
    } else {
      // Partial — clicking the parent re-includes the whole subtree.
      for (const c of subtree) next.delete(c);
    }

    persistExcluded(next);
  };

  const setAllFolders = (allIncluded: boolean) => {
    if (allIncluded) {
      persistExcluded(new Set());
    } else {
      // Exclude every top-level code; ancestor walk handles the rest.
      const next = new Set<string>();
      for (const root of folderTree) next.add(root.code);
      persistExcluded(next);
    }
  };

  const toggleExpand = (code: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const togglePanel = () => {
    setPanelOpen((prev) => {
      const next = !prev;
      saveIRLMatchPanelOpen(projectId, next);
      return next;
    });
  };

  // Summary line for the collapsed header.
  const exclusionSummary = useMemo(() => {
    if (folderTree.length === 0) return "no folder structure detected";
    if (excludedFolders.size === 0) return "matching all folders";
    // Tristate counts so the message reflects what's actually selected.
    let included = 0;
    let partial = 0;
    for (const root of folderTree) {
      const state = nodeExclusion(root, excludedFolders);
      if (state === "included") included += 1;
      else if (state === "partial") partial += 1;
    }
    const selectedAtAll = included + partial;
    if (selectedAtAll === 0) {
      return `no folders selected — pick at least one below to enable matching`;
    }
    return `${selectedAtAll} of ${folderTree.length} top-level folders selected for matching`;
  }, [folderTree, excludedFolders]);

  const importing = parsed !== null;
  const hasIRL = meta !== null && requests.length > 0;

  // ── File / sheet parse ────────────────────────────────────────────────────
  const runParse = async (file: File, sheetName?: string) => {
    setParsing(true);
    setParseError(null);
    try {
      const result = await parseIRLFile(file, sheetName);
      // Smarter header detection: pick the row whose mapping recognizes the
      // most IRL fields (especially "request"). Falls through to the
      // parser's naive guess if nothing better is found.
      const initialHeaderRow = findBestHeaderRow(result.matrix);
      const initialHeaders = result.matrix[initialHeaderRow] ?? [];
      const initialMapping = suggestMapping(initialHeaders);
      // Pad to numCols in case the suggested header row is short
      while (initialMapping.length < result.numCols) initialMapping.push("");

      setParsed(result);
      setHeaderRowIdx(initialHeaderRow);
      setMapping(initialMapping);
      setManualMapping(new Array(result.numCols).fill(false));
      setImportFile(file);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setParseError(msg || "Failed to parse file");
    } finally {
      setParsing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const file = input.files?.[0];
    input.value = "";
    if (file) void runParse(file);
  };

  const handleSheetChange = (sheetName: string) => {
    if (!importFile) return;
    void runParse(importFile, sheetName);
  };

  const handleMappingChange = (idx: number, value: IRLFieldKey | "") => {
    setMapping((prev) => {
      const next = [...prev];
      // Enforce unique field assignments — clear any other column already mapped to the same field
      if (value) {
        for (let i = 0; i < next.length; i++) {
          if (i !== idx && next[i] === value) next[i] = "";
        }
      }
      next[idx] = value;
      return next;
    });
    setManualMapping((prev) => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });
  };

  const handleSetHeaderRow = (rowIdx: number) => {
    if (!parsed) return;
    setHeaderRowIdx(rowIdx);
    // Recompute auto-suggestions for columns the user hasn't manually set
    const newHeaders = parsed.matrix[rowIdx] ?? [];
    const suggestion = suggestMapping(newHeaders);
    setMapping((prev) =>
      prev.map((cur, i) => (manualMapping[i] ? cur : (suggestion[i] ?? "")))
    );
  };

  const previewRequests = useMemo<IRLRequest[]>(() => {
    if (!parsed) return [];
    const dataRows = parsed.matrix.slice(headerRowIdx + 1);
    // Pass header+1 as the offset so each request's `originalRowIndex` is
    // its absolute row in the source matrix — Export uses this to update
    // cells in-place on the original workbook.
    return buildRequests(dataRows, mapping, headerRowIdx + 1);
  }, [parsed, headerRowIdx, mapping]);

  const handleSave = async () => {
    if (!parsed || !importFile) return;
    if (!isMappingValid(mapping)) {
      setParseError("Map a column to 'Request' before saving.");
      return;
    }
    if (previewRequests.length === 0) {
      setParseError("No rows would be saved — check the mapping and header row.");
      return;
    }
    // Capture the file's column layout AND original bytes so Export can
    // round-trip into the exact same shape (formatting + unmapped cells)
    // the user uploaded.
    const headers = (parsed.matrix[headerRowIdx] ?? []).map((h) => String(h ?? ""));
    const lastDataRowIdx = previewRequests.reduce(
      (max, r) => (typeof r.originalRowIndex === "number" && r.originalRowIndex > max ? r.originalRowIndex : max),
      headerRowIdx,
    );
    // The exact source rows that produced requests — Export uses this to
    // tell user-deletions apart from blank-separator rows that were never
    // imported. Without it, every blank gap in the source looks like a
    // deletion and forces ExcelJS to rewrite the whole workbook.
    const importedRowIndices = previewRequests
      .map((r) => r.originalRowIndex)
      .filter((i): i is number => typeof i === "number");
    let fileBytes: string | undefined;
    try {
      const buf = await importFile.arrayBuffer();
      fileBytes = arrayBufferToBase64(buf);
    } catch {
      // If we can't read the bytes, save without them — Export falls back to
      // the built-from-scratch layout. Better to save than to fail outright.
      fileBytes = undefined;
    }
    const savedMeta = saveIRLRequests(projectId, previewRequests, importFile.name, {
      headers,
      mapping,
      fileBytes,
      headerRowIdx,
      lastDataRowIdx,
      importedRowIndices,
    });
    setRequests(previewRequests);
    setMeta(savedMeta);
    setParsed(null);
    setImportFile(null);
    setParseError(null);
  };

  const handleCancelImport = () => {
    setParsed(null);
    setImportFile(null);
    setParseError(null);
  };

  const handleClearSaved = () => {
    if (!confirm("Remove the saved IRL for this project? This can't be undone.")) return;
    clearIRLRequests(projectId);
    setRequests([]);
    setMeta(null);
  };

  // ── Inline editing / row mutations ──────────────────────────────────
  const persistRequests = (next: IRLRequest[]) => {
    const fileName = meta?.originalFileName || "Edited";
    const savedMeta = saveIRLRequests(projectId, next, fileName);
    setRequests(next);
    setMeta(savedMeta);
  };

  const updateRequest = (id: string, patch: Partial<IRLRequest>) => {
    // Track which fields the user has touched so Export only writes those
    // cells back — everything else stays byte-identical to the source file.
    // Internal bookkeeping keys (editedFields, rawValues, id, originalRowIndex)
    // don't count as user edits.
    const TRACKABLE: IRLFieldKey[] = ["number", "topic", "entityName", "advisor", "request", "status", "priority"];
    persistRequests(
      requests.map((r) => {
        if (r.id !== id) return r;
        const next: IRLRequest = { ...r, ...patch };
        const dirty = new Set<IRLFieldKey>(r.editedFields ?? []);
        for (const k of TRACKABLE) {
          if (k in patch) dirty.add(k);
        }
        next.editedFields = Array.from(dirty);
        return next;
      }),
    );
  };

  const deleteRequest = (id: string) => {
    persistRequests(requests.filter((r) => r.id !== id));
  };

  const addRequest = () => {
    const blank: IRLRequest = {
      id: `manual-${Date.now()}`,
      number: "",
      topic: "",
      entityName: "",
      advisor: "",
      request: "",
      status: "open",
      priority: "medium",
    };
    persistRequests([...requests, blank]);
    // New rows go straight into edit mode — that's the only reason you'd add one.
    setEditingRowIds((prev) => new Set(prev).add(blank.id));
  };

  /**
   * Export to .xlsx — three simple rules:
   *   1. Updated rows: only write cells the user actually edited (tracked
   *      via editedFields). Untouched cells stay byte-identical.
   *   2. New rows (no originalRowIndex): append AFTER the last data row,
   *      cloning style+height from the row above one cell at a time.
   *      Write only mapped fields.
   *   3. Deleted rows: blank the mapped cells in place. We don't splice —
   *      splicing breaks merges, data validations, and named ranges.
   *
   * Filter state is irrelevant — every request is considered. Filtering is
   * a view-only affordance.
   *
   * Fallback (generated IRLs / pre-bytes saves): 7-column clean layout.
   */
  const handleExport = async () => {
    if (requests.length === 0) return;

    const fileBytes = meta?.originalFileBytes;
    const mappingLayout = meta?.originalMapping;
    const headerRowIdxOrig = meta?.originalHeaderRowIdx;
    const lastDataRowIdxOrig = meta?.originalLastDataRowIdx;
    const importedIndicesOrig = meta?.originalImportedRowIndices;

    const canRoundTrip =
      typeof fileBytes === "string" &&
      Array.isArray(mappingLayout) &&
      typeof headerRowIdxOrig === "number" &&
      typeof lastDataRowIdxOrig === "number";

    // Value to write for a (request, field) pair. If the user edited the
    // field, we write the current UI value (normalized for status/priority).
    // If they didn't, we write back the original raw text we captured at
    // upload — so "Done" stays "Done", not "Fulfilled".
    const currentValue = (r: IRLRequest, field: IRLFieldKey): string => {
      switch (field) {
        case "number": return r.number;
        case "topic": return r.topic ?? "";
        case "entityName": return r.entityName ?? "";
        case "advisor": return r.advisor ?? "";
        case "request": return r.request;
        case "status": return STATUS_COLORS[r.status]?.label ?? r.status;
        case "priority": return r.priority ? PRIORITY_COLORS[r.priority]?.label ?? r.priority : "";
      }
    };

    const baseName = (meta?.originalFileName || "IRL").replace(/\.(xlsx|xls|csv)$/i, "");
    const triggerDownload = (data: ArrayBuffer | Uint8Array, filename: string) => {
      const blob = new Blob([data as BlobPart], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    if (canRoundTrip) {
      try {
        // Split current requests into "still tied to an original row" and
        // "newly added since upload".
        const originalById = new Map<number, IRLRequest>();
        const newRequests: IRLRequest[] = [];
        for (const r of requests) {
          if (typeof r.originalRowIndex === "number") {
            originalById.set(r.originalRowIndex, r);
          } else {
            newRequests.push(r);
          }
        }

        const hasEdits = requests.some((r) => (r.editedFields?.length ?? 0) > 0);
        // A row counts as deleted ONLY if it was actually imported. Blank
        // separator rows in the source were never imported and must not
        // trigger the round-trip. If the saved meta predates this field,
        // fall back to the old range-based check (legacy behavior).
        const missingImportedIndices: number[] = [];
        const hasDeletes = (() => {
          if (Array.isArray(importedIndicesOrig)) {
            for (const idx of importedIndicesOrig) {
              if (!originalById.has(idx)) missingImportedIndices.push(idx);
            }
            return missingImportedIndices.length > 0;
          }
          for (let m = headerRowIdxOrig! + 1; m <= lastDataRowIdxOrig!; m++) {
            if (!originalById.has(m)) missingImportedIndices.push(m);
          }
          return missingImportedIndices.length > 0;
        })();
        const hasNew = newRequests.length > 0;
        void missingImportedIndices; // (kept for any future diagnostic; not used at runtime)

        // No-op fast path: nothing to change → ship the original bytes
        // verbatim. No round-trip means Excel cannot complain about any sheet.
        if (!hasEdits && !hasDeletes && !hasNew) {
          triggerDownload(base64ToArrayBuffer(fileBytes!), `${baseName} - edited.xlsx`);
          return;
        }

        // Surgical XML edit: open the .xlsx as a ZIP, edit ONLY the primary
        // sheet's XML in place, repackage. Every other sheet stays byte-
        // identical — no ExcelJS load/save corruption of unrelated sheets.
        const importedIndicesForSurgical =
          importedIndicesOrig ??
          (() => {
            const list: number[] = [];
            for (let m = headerRowIdxOrig! + 1; m <= lastDataRowIdxOrig!; m++) list.push(m);
            return list;
          })();
        const outBytes = await roundTripSurgical({
          fileBytes: base64ToArrayBuffer(fileBytes!),
          mapping: mappingLayout!,
          originalById,
          importedRowIndices: importedIndicesForSurgical,
          newRequests,
          lastDataRowIdx: lastDataRowIdxOrig!,
          valueFor: currentValue,
        });
        triggerDownload(outBytes, `${baseName} - edited.xlsx`);
        return;
      } catch (err) {
        console.error("[handleExport] surgical round-trip failed:", err);
        alert(
          `Export hit an issue rewriting the original file (${err instanceof Error ? err.message : String(err)}). Falling back to a clean layout.`,
        );
        // fall through to fallback below
      }
    }

    // Fallback: built-from-scratch layout.
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("IRL");
    ws.addRow(["IRL #", "Topic", "Entity Name", "Advisor", "Request", "Priority", "Status"]);
    for (const r of requests) {
      ws.addRow([
        r.number,
        r.topic ?? "",
        r.entityName ?? "",
        r.advisor ?? "",
        r.request,
        r.priority ? PRIORITY_COLORS[r.priority]?.label ?? r.priority : "",
        STATUS_COLORS[r.status]?.label ?? r.status,
      ]);
    }
    ws.columns = [
      { width: 10 },
      { width: 18 },
      { width: 22 },
      { width: 18 },
      { width: 70 },
      { width: 10 },
      { width: 12 },
    ];
    ws.getColumn(5).alignment = { wrapText: true, vertical: "top" };
    const out = await wb.xlsx.writeBuffer();
    triggerDownload(out as ArrayBuffer, `${baseName} - edited.xlsx`);
  };

  // ── Status counts for review header ───────────────────────────────────────
  const counts = useMemo(() => {
    const c: Record<IRLStatus, number> = { open: 0, fulfilled: 0, partial: 0, withdrawn: 0 };
    for (const r of requests) c[r.status]++;
    return c;
  }, [requests]);

  // Distinct non-empty values per categorical column, sorted, for filter
  // dropdowns. Recomputed when requests change.
  const advisorOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of requests) if (r.advisor) s.add(r.advisor);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [requests]);

  const entityOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of requests) if (r.entityName) s.add(r.entityName);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [requests]);

  const topicOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of requests) if (r.topic) s.add(r.topic);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [requests]);

  const filteredRequests = useMemo(() => {
    const q = requestSearch.trim().toLowerCase();
    return requests.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (advisorFilter !== "all" && (r.advisor ?? "") !== advisorFilter) return false;
      if (entityFilter !== "all" && (r.entityName ?? "") !== entityFilter) return false;
      if (topicFilter !== "all" && (r.topic ?? "") !== topicFilter) return false;
      if (priorityFilter !== "all" && (r.priority ?? "medium") !== priorityFilter) return false;
      if (q) {
        const hay = `${r.number} ${r.topic ?? ""} ${r.entityName ?? ""} ${r.advisor ?? ""} ${r.request}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [requests, statusFilter, advisorFilter, entityFilter, topicFilter, priorityFilter, requestSearch]);

  const hasActiveFilter =
    statusFilter !== "all" ||
    advisorFilter !== "all" ||
    entityFilter !== "all" ||
    topicFilter !== "all" ||
    priorityFilter !== "all" ||
    requestSearch.trim() !== "";

  const clearAllFilters = () => {
    setStatusFilter("all");
    setAdvisorFilter("all");
    setEntityFilter("all");
    setTopicFilter("all");
    setPriorityFilter("all");
    setRequestSearch("");
  };

  // ── Document Match (Phase 1: name-based) ──────────────────────────────

  /**
   * Build the catalog from a single subtree fetched at a top-level
   * selected folder. Path is prefixed with `rootLabel` so files like
   * "1.0 Financial/AR Aging/foo.xlsx" carry their full SP context.
   * Exclusion still applies inside the subtree — the user might have
   * unchecked a sub-folder beneath the top-level they kept.
   */
  const collectSubtreeFiles = (
    subtree: SPTreeNode,
    rootLabel: string,
    rootCode: string,
    itemIdToCode: Record<string, string>,
  ): { path: string; fileName: string; webUrl: string }[] => {
    const out: { path: string; fileName: string; webUrl: string }[] = [];

    const walk = (node: SPTreeNode, ancestors: string[], deepestCode: string) => {
      const myCode = itemIdToCode[node.id] ?? deepestCode;
      if (isCodeExcluded(myCode, excludedFolders)) return;

      const folderPath = ancestors.join("/");
      for (const f of node.files ?? []) {
        out.push({
          path: folderPath ? `${folderPath}/${f.name}` : f.name,
          fileName: f.name,
          webUrl: f.webUrl ?? "",
        });
      }
      for (const child of node.children) {
        walk(child, [...ancestors, child.name], myCode);
      }
    };

    // The synthetic root represents the top-level folder itself — its files
    // are the ones directly inside; its children are subfolders.
    walk(subtree, [rootLabel], rootCode);
    return out;
  };

  const handleFindDocuments = async () => {
    if (requests.length === 0) {
      setSearchError("Save an IRL list first.");
      return;
    }
    const folder = getFolderRef(projectId);
    if (!folder) {
      setSearchError("This project isn't linked to a SharePoint folder yet — set one up in the VDR tab.");
      return;
    }

    // Plain-English plan:
    // 1. Look at every code we know about (indexMap) and keep the ones
    //    the user didn't exclude.
    // 2. From those, take only the top-level kept codes — codes whose
    //    parent isn't also kept (otherwise we'd double-fetch its subtree).
    // 3. Fetch each of those subtrees in parallel. Walk only inside them.
    // No SP scanning of folders the user didn't choose.
    const indexMap = getIndexMap(projectId) || {};
    const indexRows = getIndexRows(projectId) || [];
    const titleByCode = new Map<string, string>();
    for (const r of indexRows) titleByCode.set(r.code, r.title);

    const itemIdToCode: Record<string, string> = {};
    for (const [code, itemId] of Object.entries(indexMap)) itemIdToCode[itemId] = code;

    const kept: { code: string; itemId: string }[] = [];
    for (const [code, itemId] of Object.entries(indexMap)) {
      if (!isCodeExcluded(code, excludedFolders)) kept.push({ code, itemId });
    }
    const keptCodes = new Set(kept.map((k) => k.code));
    const topLevel = kept.filter((k) => {
      const i = k.code.lastIndexOf(".");
      const parent = i === -1 ? null : k.code.slice(0, i);
      return !parent || !keptCodes.has(parent);
    });

    if (topLevel.length === 0) {
      setSearchError("No folders selected — open the \"Folders to match\" picker and check at least one folder.");
      return;
    }

    setSearching(true);
    setSearchError(null);
    setSearchProgress(`Scanning ${topLevel.length} selected folder${topLevel.length === 1 ? "" : "s"}…`);

    try {
      const subtrees = await Promise.all(
        topLevel.map((t) => fetchSharePointTree(folder.driveId, t.itemId, undefined, true)),
      );

      const catalog: { path: string; fileName: string; webUrl: string }[] = [];
      topLevel.forEach((t, i) => {
        const label = titleByCode.get(t.code) || t.code;
        catalog.push(...collectSubtreeFiles(subtrees[i], label, t.code, itemIdToCode));
      });
      if (catalog.length === 0) {
        setSearchError("No files found in the selected folders.");
        return;
      }

      setSearchProgress(`Asking Claude to match ${catalog.length} files against ${requests.length} IRL requests…`);

      const res = await apiFetch("/api/irl/document-match", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: requests.map((r) => ({
            id: r.id,
            number: r.number,
            topic: r.topic,
            entityName: r.entityName,
            request: r.request,
          })),
          catalog: catalog.map((c) => ({ path: c.path, fileName: c.fileName })),
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Match failed (HTTP ${res.status})`);
      }

      const data = (await res.json()) as { matches: Record<string, { path: string; confidence: "high" | "medium" | "low"; reasoning: string }[]> };

      // Stitch the server's path-only matches back into full DocumentMatch
      // objects with file name + webUrl from the catalog.
      const byPath = new Map(catalog.map((c) => [c.path, c]));
      const now = new Date().toISOString();
      const next: Record<string, IRLAnswer> = {};
      for (const r of requests) {
        const raw = data.matches[r.id] ?? [];
        const matches: DocumentMatch[] = raw
          .map((m) => {
            const c = byPath.get(m.path);
            return {
              path: m.path,
              fileName: c?.fileName ?? m.path.split("/").pop() ?? m.path,
              webUrl: c?.webUrl ?? "",
              confidence: m.confidence,
              reasoning: m.reasoning,
            };
          });
        next[r.id] = { matches, mode: "structural", lastChecked: now };
      }
      saveIRLAnswers(projectId, next);
      setAnswers(next);
      setSearchProgress(`Done. ${countMatches(next)} candidate matches across ${countWithMatches(next)} of ${requests.length} requests.`);
    } catch (e: any) {
      setSearchError(e?.message || "Document match failed");
      setSearchProgress(null);
    } finally {
      setSearching(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, color: "#2f6b2f", fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22 }}>📋</span>
            Information Request List
          </h3>
          <div style={{ fontSize: 13, color: "#4a7d4a", marginTop: 4 }}>
            Upload an IRL once per project. The system normalizes it so future automations always know which column is which.
          </div>
        </div>

        {hasIRL && !importing && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => void handleFindDocuments()}
              disabled={searching}
              title="Search the project's VDR for documents that look like they answer each IRL request (name-based, no document content read)."
              style={{
                background: searching ? "#9bbf8f" : "#15803d",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: searching ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {searching ? "Searching…" : "🔎 Find documents"}
            </button>
            {Object.keys(answers).length > 0 && (
              <button
                onClick={() => {
                  clearIRLAnswers(projectId);
                  setAnswers({});
                  setSearchProgress(null);
                  setSearchError(null);
                }}
                title="Hide all document-match results (the IRL list returns to its plain view)."
                style={{
                  background: "#fff",
                  color: "#075985",
                  border: "1px solid #bae6fd",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                Clear matches
              </button>
            )}
            <button
              onClick={() => { setNotesOpen((v) => !v); setDatabookOpen(false); }}
              title="Paste call notes — Claude pulls follow-ups and adds them to the IRL."
              style={{
                background: notesOpen ? "#15803d" : "#ffffff",
                color: notesOpen ? "#fff" : "#2f6b2f",
                border: `1px solid ${notesOpen ? "#15803d" : "#cfe6c7"}`,
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              📝 Add from notes
            </button>
            <button
              onClick={() => { setDatabookOpen((v) => !v); setNotesOpen(false); }}
              disabled={!canImportFromDatabook}
              title={
                canImportFromDatabook
                  ? "Import trending questions marked for IRL from the pinned databook."
                  : "No databook pinned with an IRL column. Rebuild the databook to enable this."
              }
              style={{
                background: databookOpen ? "#15803d" : "#ffffff",
                color: databookOpen ? "#fff" : canImportFromDatabook ? "#2f6b2f" : "#9ca3af",
                border: `1px solid ${databookOpen ? "#15803d" : canImportFromDatabook ? "#cfe6c7" : "#e5e7eb"}`,
                borderRadius: 8,
                padding: "8px 12px",
                cursor: canImportFromDatabook ? "pointer" : "not-allowed",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              📥 Import from databook
            </button>
            <button
              onClick={handleExport}
              title="Download the current IRL as .xlsx"
              style={{
                background: "#ffffff",
                color: "#2f6b2f",
                border: "1px solid #cfe6c7",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              📤 Export
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: "#ffffff",
                color: "#2f6b2f",
                border: "1px solid #cfe6c7",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              Replace IRL
            </button>
            <button
              onClick={handleClearSaved}
              style={{
                background: "#fff",
                color: "#dc2626",
                border: "1px solid #fca5a5",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Document Match progress / error / done line */}
      {(searchProgress || searchError) && (
        <div
          style={{
            background: searchError ? "#fef2f2" : "#f0f9ff",
            border: `1px solid ${searchError ? "#fca5a5" : "#bae6fd"}`,
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 12,
            color: searchError ? "#991b1b" : "#075985",
          }}
        >
          {searchError ?? searchProgress}
        </div>
      )}

      {/* Inline notes-adder (paste call notes → review → append to IRL). */}
      {hasIRL && !importing && notesOpen && (
        <IRLNotesAdder
          projectId={projectId}
          onAdded={(addedIds) => {
            setRequests(getIRLRequests(projectId));
            setMeta(getIRLMeta(projectId));
            setRecentlyAddedIds(new Set(addedIds));
            setNotesOpen(false);
          }}
          onCancel={() => setNotesOpen(false)}
        />
      )}

      {hasIRL && !importing && databookOpen && (
        <IRLDatabookAdder
          projectId={projectId}
          onAdded={(addedIds) => {
            setRequests(getIRLRequests(projectId));
            setMeta(getIRLMeta(projectId));
            setRecentlyAddedIds(new Set(addedIds));
            setDatabookOpen(false);
          }}
          onCancel={() => setDatabookOpen(false)}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {parseError && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            padding: 10,
            fontSize: 13,
            color: "#991b1b",
          }}
        >
          {parseError}
        </div>
      )}

      {/* ── Empty state (no IRL, not importing) ─────────────────────────── */}
      {!hasIRL && !importing && (
        <div
          style={{
            padding: 32,
            border: "1px dashed #cfe6c7",
            borderRadius: 10,
            background: "#f8fdf6",
            textAlign: "center",
            color: "#4a7d4a",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No IRL uploaded yet</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
            Upload the buyer's IRL spreadsheet (.xlsx, .xls, or .csv). You'll then map each column to our standardized fields.
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={parsing}
            style={{
              background: parsing ? "#9bbf8f" : "#6aa84f",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 14px",
              cursor: parsing ? "not-allowed" : "pointer",
              fontWeight: 700,
            }}
          >
            {parsing ? "Parsing…" : "📄 Upload IRL"}
          </button>
        </div>
      )}

      {/* ── Mapping / preview state ─────────────────────────────────────── */}
      {importing && parsed && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* File + sheet bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
              background: "#f8fdf6",
              border: "1px solid #cfe6c7",
              borderRadius: 8,
              padding: "10px 12px",
            }}
          >
            <div style={{ fontSize: 13, color: "#2f6b2f" }}>
              <strong>File:</strong>{" "}
              <span style={{ fontFamily: "monospace" }}>{importFile?.name}</span>
              {" · "}
              <strong>{parsed.matrix.length}</strong> total row{parsed.matrix.length === 1 ? "" : "s"}
              {" · header row "}
              <strong>{headerRowIdx + 1}</strong>
            </div>
            {parsed.sheetNames.length > 1 && (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <label style={{ fontSize: 12, color: "#4a7d4a" }}>Sheet:</label>
                <select
                  value={parsed.activeSheet}
                  onChange={(e) => handleSheetChange(e.target.value)}
                  style={{
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid #cfe6c7",
                    fontSize: 12,
                  }}
                >
                  {parsed.sheetNames.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div style={{ fontSize: 12, color: "#4a7d4a" }}>
            Pick a standardized field for each column at the top. Click any row number to mark it as the header
            row — rows above it will be ignored.
          </div>

          {/* Unified grid: column-mapper row at top, then file rows */}
          <div
            style={{
              border: "1px solid #cfe6c7",
              borderRadius: 8,
              overflow: "auto",
              maxHeight: 520,
              background: "#fff",
            }}
          >
            <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12, width: "max-content", minWidth: "100%" }}>
              <thead>
                {/* Column-mapper row — sticky at top, visually separated by a thicker bottom border */}
                <tr style={{ background: "#eaf7e6" }}>
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      left: 0,
                      zIndex: 3,
                      background: "#eaf7e6",
                      borderBottom: "1px solid #cfe6c7",
                      borderRight: "1px solid #cfe6c7",
                      padding: "8px 6px",
                      width: 48,
                      minWidth: 48,
                      fontSize: 11,
                      color: "#2f6b2f",
                      fontWeight: 700,
                    }}
                  >
                    #
                  </th>
                  {Array.from({ length: parsed.numCols }, (_, c) => {
                    const isAssigned = !!mapping[c];
                    return (
                      <th
                        key={c}
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          background: isAssigned ? "#dff3d9" : "#eaf7e6",
                          borderBottom: "1px solid #cfe6c7",
                          borderRight: "1px solid #e5f2e0",
                          padding: "8px",
                          minWidth: COL_MIN_WIDTH,
                          textAlign: "left",
                        }}
                      >
                        <select
                          value={mapping[c] ?? ""}
                          onChange={(e) => handleMappingChange(c, e.target.value as IRLFieldKey | "")}
                          style={{
                            width: "100%",
                            padding: "5px 6px",
                            borderRadius: 6,
                            border: `1px solid ${isAssigned ? "#6aa84f" : "#cfe6c7"}`,
                            background: "#fff",
                            fontSize: 12,
                            fontWeight: isAssigned ? 700 : 500,
                            color: isAssigned ? "#15803d" : "#4a7d4a",
                          }}
                        >
                          <option value="">— Ignore —</option>
                          {IRL_FIELDS.map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                              {f.required ? " *" : ""}
                            </option>
                          ))}
                        </select>
                      </th>
                    );
                  })}
                </tr>
                {/* Visual separator row between mapper and file content */}
                <tr style={{ height: 6 }}>
                  <td
                    colSpan={parsed.numCols + 1}
                    style={{
                      padding: 0,
                      borderBottom: "2px solid #6aa84f",
                      background: "#eaf7e6",
                    }}
                  />
                </tr>
              </thead>
              <tbody>
                {parsed.matrix.slice(0, PREVIEW_ROW_LIMIT).map((row, r) => {
                  const isHeader = r === headerRowIdx;
                  const isAbove = r < headerRowIdx;
                  const rowBg = isHeader ? "#dff3d9" : isAbove ? "#f9fafb" : "#ffffff";
                  const rowColor = isAbove ? "#9ca3af" : "#333";
                  return (
                    <tr key={r} style={{ background: rowBg }}>
                      <td
                        onClick={() => handleSetHeaderRow(r)}
                        title={
                          isHeader
                            ? "This is the header row"
                            : "Click to mark this as the header row"
                        }
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          background: isHeader ? "#bfe5ad" : isAbove ? "#f3f4f6" : "#f4fbf1",
                          color: isHeader ? "#15803d" : "#6b7280",
                          fontWeight: isHeader ? 700 : 500,
                          textAlign: "center",
                          padding: "6px 6px",
                          minWidth: 48,
                          width: 48,
                          borderRight: `1px solid ${isHeader ? "#6aa84f" : "#cfe6c7"}`,
                          borderBottom: "1px solid #f0f4ee",
                          cursor: "pointer",
                          userSelect: "none",
                          fontFamily: "monospace",
                          fontSize: 11,
                        }}
                      >
                        {r + 1}
                      </td>
                      {row.map((cell, c) => {
                        const colAssigned = !!mapping[c];
                        return (
                          <td
                            key={c}
                            title={cell}
                            style={{
                              padding: "6px 10px",
                              minWidth: COL_MIN_WIDTH,
                              maxWidth: 320,
                              borderRight: "1px solid #f0f4ee",
                              borderBottom: "1px solid #f0f4ee",
                              color: rowColor,
                              fontWeight: isHeader ? 700 : 400,
                              fontStyle: isAbove ? "italic" : "normal",
                              background: !isHeader && !isAbove && colAssigned ? "#fbfffa" : undefined,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {cell || (
                              <span style={{ color: "#cbd5e1" }}>—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {parsed.matrix.length > PREVIEW_ROW_LIMIT && (
                  <tr>
                    <td
                      colSpan={parsed.numCols + 1}
                      style={{
                        padding: "10px 12px",
                        textAlign: "center",
                        color: "#6b7280",
                        fontStyle: "italic",
                        background: "#f9fafb",
                        borderTop: "1px solid #e5e7eb",
                      }}
                    >
                      Showing first {PREVIEW_ROW_LIMIT} of {parsed.matrix.length} rows. All rows below the
                      header will be saved when you confirm.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Action row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 12, color: "#4a7d4a" }}>
              Will save <strong>{previewRequests.length}</strong> request
              {previewRequests.length === 1 ? "" : "s"}
              {!isMappingValid(mapping) && (
                <span style={{ color: "#b7950b", marginLeft: 8 }}>
                  · map a column to <strong>Request</strong> to enable save
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={handleCancelImport}
                style={{
                  background: "#e0e0e0",
                  color: "#333",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 16px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!isMappingValid(mapping) || previewRequests.length === 0}
                style={{
                  background:
                    !isMappingValid(mapping) || previewRequests.length === 0
                      ? "#9bbf8f"
                      : "#15803d",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 16px",
                  cursor:
                    !isMappingValid(mapping) || previewRequests.length === 0
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 700,
                }}
              >
                ✅ Save IRL ({previewRequests.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Folder picker (collapsible). Only meaningful once an IRL list exists. ── */}
      {hasIRL && !importing && (
        <div
          style={{
            background: "#f8fdf6",
            border: "1px solid #cfe6c7",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {/* Collapsible header — toggle on the left, Select all / Clear on the right.
              Two side-by-side elements (button + div) instead of buttons-inside-button. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: "10px 14px",
              borderBottom: panelOpen ? "1px solid #cfe6c7" : "none",
              color: "#2f6b2f",
              fontSize: 13,
            }}
          >
            <button
              type="button"
              onClick={togglePanel}
              style={{
                flex: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "transparent",
                border: "none",
                padding: 0,
                margin: 0,
                textAlign: "left",
                cursor: "pointer",
                color: "inherit",
                font: "inherit",
              }}
            >
              <span style={{ fontSize: 11, color: "#4a7d4a", width: 12, display: "inline-block" }}>
                {panelOpen ? "▾" : "▸"}
              </span>
              <span style={{ fontWeight: 600 }}>Folders to match</span>
              <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 400 }}>
                — {exclusionSummary}
              </span>
            </button>
            {panelOpen && folderTree.length > 0 && (
              <div style={{ display: "inline-flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setAllFolders(true)}
                  style={{
                    background: "#fff",
                    color: "#2f6b2f",
                    border: "1px solid #cfe6c7",
                    borderRadius: 6,
                    padding: "3px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => setAllFolders(false)}
                  style={{
                    background: "#fff",
                    color: "#4a7d4a",
                    border: "1px solid #cfe6c7",
                    borderRadius: 6,
                    padding: "3px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {panelOpen && (
            <div style={{ padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "#4a7d4a", marginBottom: 8 }}>
                Files going into unchecked folders are skipped (no Claude call) when you click "Run IRL match" — applies to both VDR drag-and-drop and Folder Mirroring.
              </div>
              {folderTree.length === 0 ? (
                <div style={{ fontSize: 12, color: "#92651e", background: "#fffbeb", border: "1px solid #f5d97e", borderRadius: 6, padding: "8px 10px" }}>
                  ⓘ No folder structure loaded for this project yet — in the VDR tab, click "Sync from SharePoint" (or "Create Folders from Index"). Picking a folder alone doesn't load the structure, and it's stored per browser.
                </div>
              ) : (
                <div role="tree" style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {folderTree.map((node) => (
                    <FolderTreeRow
                      key={node.code}
                      node={node}
                      excluded={excludedFolders}
                      expanded={expanded}
                      onToggleExpand={toggleExpand}
                      onToggleNode={toggleNode}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Review state ────────────────────────────────────────────────── */}
      {hasIRL && !importing && meta && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Meta */}
          <div
            style={{
              background: "#f8fdf6",
              border: "1px solid #cfe6c7",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: 12,
              color: "#4a7d4a",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div>
              <strong>{requests.length}</strong> requests · uploaded{" "}
              {new Date(meta.uploadedAt).toLocaleString()} from{" "}
              <span style={{ fontFamily: "monospace" }}>{meta.originalFileName}</span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(["all", "open", "partial", "fulfilled", "withdrawn"] as const).map((s) => {
                const isActive = statusFilter === s;
                const total =
                  s === "all" ? requests.length : counts[s as IRLStatus];
                const baseLabel = s === "all" ? "All" : STATUS_COLORS[s as IRLStatus].label;
                return (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    style={{
                      background: isActive ? "#6aa84f" : "#fff",
                      color: isActive ? "#fff" : "#2f6b2f",
                      border: "1px solid #cfe6c7",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {baseLabel} ({total})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Column filters — each row stays tied to its originalRowIndex,
              so toggling filters never changes the export behavior. */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #cfe6c7",
              borderRadius: 8,
              padding: "8px 10px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
            }}
          >
            <span style={{ fontWeight: 600, color: "#2f6b2f" }}>Filter:</span>

            <FilterSelect
              label="Advisor"
              value={advisorFilter}
              options={advisorOptions}
              onChange={setAdvisorFilter}
              quickPick={advisorOptions.find((a) => /leo\s*berwick|^lb$/i.test(a))}
              quickPickLabel="LB only"
            />

            <FilterSelect
              label="Entity"
              value={entityFilter}
              options={entityOptions}
              onChange={setEntityFilter}
            />

            <FilterSelect
              label="Topic"
              value={topicFilter}
              options={topicOptions}
              onChange={setTopicFilter}
            />

            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <label style={{ color: "#4a7d4a" }}>Priority:</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as "all" | IRLPriority)}
                style={filterSelectStyle}
              >
                <option value="all">All</option>
                {(Object.keys(PRIORITY_COLORS) as IRLPriority[]).map((p) => (
                  <option key={p} value={p}>{PRIORITY_COLORS[p].label}</option>
                ))}
              </select>
            </div>

            <input
              type="text"
              placeholder="Search request text…"
              value={requestSearch}
              onChange={(e) => setRequestSearch(e.target.value)}
              style={{
                flex: "1 1 200px",
                minWidth: 160,
                padding: "5px 8px",
                border: "1px solid #cfe6c7",
                borderRadius: 6,
                fontSize: 12,
              }}
            />

            <span style={{ color: "#6b7280", fontSize: 11 }}>
              Showing <strong style={{ color: "#2f6b2f" }}>{filteredRequests.length}</strong> of{" "}
              <strong style={{ color: "#2f6b2f" }}>{requests.length}</strong>
            </span>

            {hasActiveFilter && (
              <button
                onClick={clearAllFilters}
                title="Clear all filters"
                style={{
                  background: "#fff",
                  color: "#4a7d4a",
                  border: "1px solid #cfe6c7",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Recently-added banner — shows when notes added new rows. */}
          {recentlyAddedIds.size > 0 && (
            <div
              style={{
                background: "#ecfdf5",
                border: "1px solid #6ee7b7",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                color: "#065f46",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span>
                <strong>{recentlyAddedIds.size}</strong> new request
                {recentlyAddedIds.size === 1 ? "" : "s"} just added from notes — highlighted below.
                Click <strong>Edit</strong> on any row to tweak.
              </span>
              <button
                onClick={() => setRecentlyAddedIds(new Set())}
                style={{
                  background: "#fff",
                  color: "#065f46",
                  border: "1px solid #6ee7b7",
                  borderRadius: 6,
                  padding: "3px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Clear new markers
              </button>
            </div>
          )}

          {/* Datalist of observed advisors — drives the per-row Advisor
              input's autocomplete so typing an existing advisor is one tap. */}
          <datalist id={`advisor-options-${projectId}`}>
            {advisorOptions.map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>

          {/* Table */}
          <div ref={reviewTableRef} style={{ border: "1px solid #cfe6c7", borderRadius: 8, overflow: "auto", maxHeight: 520 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f4fbf1", borderBottom: "1px solid #cfe6c7", position: "sticky", top: 0, zIndex: 1 }}>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: "#2f6b2f", fontWeight: 600, width: 70 }}>IRL #</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: "#2f6b2f", fontWeight: 600, width: 110 }}>Topic</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: "#2f6b2f", fontWeight: 600, width: 130 }}>Entity Name</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: "#2f6b2f", fontWeight: 600, width: 110 }}>Advisor</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: "#2f6b2f", fontWeight: 600 }}>Request</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: "#2f6b2f", fontWeight: 600, width: 100 }}>Priority</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: "#2f6b2f", fontWeight: 600, width: 100 }}>Status</th>
                  <th style={{ width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((r) => {
                  const sc = STATUS_COLORS[r.status];
                  const answer = answers[r.id];
                  const matches = answer?.matches ?? [];
                  const isEditing = editingRowIds.has(r.id);
                  const isNew = recentlyAddedIds.has(r.id);
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        style={{
                          borderBottom: matches.length > 0 || answer ? "none" : "1px solid #e5f2e0",
                          background: isNew ? "#ecfdf5" : isEditing ? "#f8fdf6" : undefined,
                          boxShadow: isNew ? "inset 3px 0 0 0 #15803d" : undefined,
                        }}
                      >
                        <td style={cellPadStyle}>
                          {isEditing ? (
                            <input
                              type="text"
                              value={r.number}
                              onChange={(e) => updateRequest(r.id, { number: e.target.value })}
                              style={{ ...editInputStyle, fontFamily: "monospace" }}
                            />
                          ) : (
                            <span style={{ fontFamily: "monospace", color: "#4a7d4a", fontSize: 13 }}>{r.number}</span>
                          )}
                        </td>
                        <td style={cellPadStyle}>
                          {isEditing ? (
                            <input
                              type="text"
                              value={r.topic ?? ""}
                              onChange={(e) => updateRequest(r.id, { topic: e.target.value })}
                              style={editInputStyle}
                            />
                          ) : (
                            <span style={{ color: "#4a7d4a", fontSize: 13 }}>{r.topic ?? ""}</span>
                          )}
                        </td>
                        <td style={cellPadStyle}>
                          {isEditing ? (
                            <input
                              type="text"
                              value={r.entityName ?? ""}
                              onChange={(e) => updateRequest(r.id, { entityName: e.target.value })}
                              style={editInputStyle}
                            />
                          ) : (
                            <span style={{ color: "#4a7d4a", fontSize: 13 }}>{r.entityName ?? ""}</span>
                          )}
                        </td>
                        <td style={cellPadStyle}>
                          {isEditing ? (
                            <input
                              type="text"
                              value={r.advisor ?? ""}
                              onChange={(e) => updateRequest(r.id, { advisor: e.target.value })}
                              list={`advisor-options-${projectId}`}
                              style={editInputStyle}
                            />
                          ) : (
                            <span style={{ color: "#4a7d4a", fontSize: 13 }}>{r.advisor ?? ""}</span>
                          )}
                        </td>
                        <td style={cellPadStyle}>
                          {isEditing ? (
                            <textarea
                              value={r.request}
                              onChange={(e) => updateRequest(r.id, { request: e.target.value })}
                              rows={5}
                              style={{ ...editInputStyle, resize: "vertical", fontFamily: "inherit", minHeight: 90, lineHeight: 1.4 }}
                            />
                          ) : (
                            <div style={{ color: "#333", fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.4, padding: "2px 0" }}>
                              {r.request}
                            </div>
                          )}
                        </td>
                        <td style={cellPadStyle}>
                          {(() => {
                            const p: IRLPriority = r.priority ?? "medium";
                            const pc = PRIORITY_COLORS[p];
                            return isEditing ? (
                              <select
                                value={p}
                                onChange={(e) => updateRequest(r.id, { priority: e.target.value as IRLPriority })}
                                style={{
                                  ...editInputStyle,
                                  background: pc.bg,
                                  color: pc.fg,
                                  fontWeight: 600,
                                }}
                              >
                                {(Object.keys(PRIORITY_COLORS) as IRLPriority[]).map((k) => (
                                  <option key={k} value={k}>{PRIORITY_COLORS[k].label}</option>
                                ))}
                              </select>
                            ) : (
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  background: pc.bg,
                                  color: pc.fg,
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                {pc.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={cellPadStyle}>
                          {isEditing ? (
                            <select
                              value={r.status}
                              onChange={(e) => updateRequest(r.id, { status: e.target.value as IRLStatus })}
                              style={{
                                ...editInputStyle,
                                background: sc.bg,
                                color: sc.fg,
                                fontWeight: 600,
                              }}
                            >
                              {(Object.keys(STATUS_COLORS) as IRLStatus[]).map((s) => (
                                <option key={s} value={s}>{STATUS_COLORS[s].label}</option>
                              ))}
                            </select>
                          ) : (
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: 999,
                                background: sc.bg,
                                color: sc.fg,
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {sc.label}
                            </span>
                          )}
                        </td>
                        <td style={{ ...cellPadStyle, textAlign: "right" }}>
                          <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                            {isNew && (
                              <span
                                title="Added from notes in this session"
                                style={{
                                  background: "#15803d",
                                  color: "#fff",
                                  fontSize: 9,
                                  fontWeight: 700,
                                  padding: "2px 6px",
                                  borderRadius: 999,
                                  letterSpacing: 0.5,
                                }}
                              >
                                NEW
                              </span>
                            )}
                            <button
                              onClick={() => toggleEdit(r.id)}
                              title={isEditing ? "Done editing this row" : "Edit this row"}
                              style={{
                                background: isEditing ? "#15803d" : "#fff",
                                color: isEditing ? "#fff" : "#2f6b2f",
                                border: `1px solid ${isEditing ? "#15803d" : "#cfe6c7"}`,
                                borderRadius: 4,
                                padding: "3px 10px",
                                cursor: "pointer",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {isEditing ? "Done" : "Edit"}
                            </button>
                            {isEditing && (
                              <button
                                onClick={() => deleteRequest(r.id)}
                                title="Delete this row"
                                style={{
                                  background: "transparent",
                                  color: "#dc2626",
                                  border: "1px solid #fca5a5",
                                  borderRadius: 4,
                                  padding: "3px 8px",
                                  cursor: "pointer",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {matches.length > 0 && (
                        <tr style={{ background: "#f0f9ff", borderBottom: "1px solid #e5f2e0" }}>
                          <td colSpan={8} style={{ padding: "6px 10px 10px 10px" }}>
                            <div style={{ fontSize: 11, color: "#075985", marginBottom: 4 }}>
                              {matches.length} candidate document{matches.length === 1 ? "" : "s"} found in VDR:
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {matches.map((m, i) => {
                                const folder = m.path.includes("/")
                                  ? m.path.slice(0, m.path.lastIndexOf("/"))
                                  : "";
                                const cs = CONFIDENCE_STYLE[m.confidence];
                                const fileLabel = (
                                  <>
                                    <span style={{ fontFamily: "monospace", color: "#0c4a6e", fontWeight: 600 }}>
                                      ↳ {m.fileName}
                                    </span>
                                    {folder && (
                                      <span style={{ color: "#64748b", fontSize: 11, marginLeft: 6 }}>
                                        · {folder}
                                      </span>
                                    )}
                                  </>
                                );
                                return (
                                  <div
                                    key={i}
                                    title={m.reasoning}
                                    style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}
                                  >
                                    {m.webUrl ? (
                                      <a
                                        href={m.webUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: "none", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                      >
                                        {fileLabel}
                                      </a>
                                    ) : (
                                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {fileLabel}
                                      </span>
                                    )}
                                    <span
                                      style={{
                                        background: cs.bg,
                                        color: cs.fg,
                                        padding: "1px 8px",
                                        borderRadius: 999,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      {m.confidence}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                      {matches.length === 0 && answer && (
                        <tr style={{ background: "#fffbeb", borderBottom: "1px solid #e5f2e0" }}>
                          <td colSpan={8} style={{ padding: "6px 10px 10px 10px", fontSize: 12, color: "#92651e" }}>
                            ↳ No candidate documents found by name search.{" "}
                            <em style={{ color: "#a78b3f" }}>Deep document search coming in Phase 2.</em>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filteredRequests.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: 16, textAlign: "center", color: "#6b7280", fontStyle: "italic" }}>
                      No requests match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button
            onClick={addRequest}
            style={{
              alignSelf: "flex-start",
              background: "#fff",
              color: "#2f6b2f",
              border: "1px solid #cfe6c7",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            + Add row
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────── Recursive tree-row component ─────────────────────── */

type FolderTreeRowProps = {
  node: FolderNode;
  excluded: Set<string>;
  expanded: Set<string>;
  onToggleExpand: (code: string) => void;
  onToggleNode: (node: FolderNode) => void;
};

const FolderTreeRow: React.FC<FolderTreeRowProps> = ({
  node,
  excluded,
  expanded,
  onToggleExpand,
  onToggleNode,
}) => {
  const status = nodeExclusion(node, excluded);
  const isExpanded = expanded.has(node.code);
  const hasChildren = node.children.length > 0;
  // depth 1 -> indent 0; depth 2 -> indent 1; etc.
  const indent = (node.depth - 1) * 18;
  const ancestorExcluded = status === "excluded" && !excluded.has(node.code);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 4px 3px 0",
          marginLeft: indent,
          fontSize: 12,
        }}
      >
        {/* +/– toggle (or spacer when no children) */}
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggleExpand(node.code)}
            aria-label={isExpanded ? "Collapse" : "Expand"}
            style={{
              width: 18,
              height: 18,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
              border: "1px solid #cfe6c7",
              borderRadius: 4,
              color: "#2f6b2f",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              lineHeight: 1,
              padding: 0,
            }}
          >
            {isExpanded ? "–" : "+"}
          </button>
        ) : (
          <span style={{ display: "inline-block", width: 18 }} />
        )}

        {/* Tristate checkbox */}
        <input
          type="checkbox"
          checked={status === "included"}
          ref={(el) => {
            if (el) el.indeterminate = status === "partial";
          }}
          onChange={() => onToggleNode(node)}
          style={{ cursor: "pointer", margin: 0 }}
          title={
            ancestorExcluded
              ? "Excluded by a parent folder above. Re-checking here will include only this folder, leaving siblings excluded."
              : status === "partial"
                ? "Some subfolders excluded — click to re-include the whole subtree."
                : status === "excluded"
                  ? "Excluded — click to include."
                  : "Included — click to exclude."
          }
        />

        <span
          style={{
            color: status === "excluded" ? "#9ca3af" : "#15803d",
            fontWeight: 600,
            fontFamily: "monospace",
          }}
        >
          {node.code}
        </span>
        {node.title && (
          <span
            style={{
              color: status === "excluded" ? "#9ca3af" : "#374151",
              fontWeight: 400,
            }}
          >
            — {node.title}
          </span>
        )}
      </div>

      {hasChildren && isExpanded && (
        <>
          {node.children.map((child) => (
            <FolderTreeRow
              key={child.code}
              node={child}
              excluded={excluded}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              onToggleNode={onToggleNode}
            />
          ))}
        </>
      )}
    </>
  );
};

export default IRLReviewPanel;
